import { RightChevronIcon } from "components/atoms/Icon";
import { MenuItem } from "components/organisms/Chatbot/ChatbotSidebar/ChatbotSidebar.constants";
import { useClickOutside } from "hooks/useClickOutside";
import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./NestedDropdown.module.scss";

interface NestedDropdownProps {
    trigger: React.ReactNode;
    items: MenuItem[];
    onSelect: (id: string) => void;
    disabled?: boolean;
}

function NestedDropdown({ trigger, items, onSelect, disabled = false }: NestedDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useClickOutside(containerRef, () => setIsOpen(false));

    const handleToggle = () => {
        if (!disabled) {
            if (!isOpen && triggerRef.current) {
                setTriggerRect(triggerRef.current.getBoundingClientRect());
            }
            setIsOpen(!isOpen);
        }
    };

    const handleSelect = (id: string) => {
        onSelect(id);
        setIsOpen(false);
    };

    return (
        <>
            <div className={styles.container} ref={containerRef}>
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={handleToggle}
                    className={`${styles.trigger} ${disabled ? styles.disabled : ""}`}
                    disabled={disabled}
                >
                    {trigger}
                </button>
            </div>
            {isOpen &&
                !disabled &&
                triggerRect &&
                createPortal(
                    <MenuItems items={items} onSelect={handleSelect} triggerRect={triggerRect} />,
                    document.body
                )}
        </>
    );
}

interface MenuItemsProps {
    items: MenuItem[];
    onSelect: (id: string) => void;
    level?: number;
    triggerRect?: DOMRect;
}

function MenuItems({ items, onSelect, level = 0, triggerRect }: MenuItemsProps) {
    const [hoveredItem, setHoveredItem] = useState<string | null>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [submenuPositions, setSubmenuPositions] = useState<Record<string, "left" | "right">>({});

    const getSubmenuPosition = (itemElement: HTMLElement): "left" | "right" => {
        const rect = itemElement.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const submenuWidth = 240;
        const margin = 4;

        if (rect.right + submenuWidth + margin > viewportWidth) {
            return "left";
        }
        return "right";
    };

    const handleItemMouseEnter = (itemId: string, element: HTMLElement) => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }

        if (itemId !== hoveredItem) {
            setHoveredItem(itemId);
            const position = getSubmenuPosition(element);
            setSubmenuPositions((prev) => ({ ...prev, [itemId]: position }));
        }
    };

    const handleItemMouseLeave = () => {
        hideTimeoutRef.current = setTimeout(() => {
            setHoveredItem(null);
        }, 300);
    };

    const handleMenuMouseEnter = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    const handleMenuMouseLeave = () => {
        hideTimeoutRef.current = setTimeout(() => {
            setHoveredItem(null);
        }, 300);
    };

    const ESTIMATED_ITEM_HEIGHT = 44;
    const MENU_PADDING = 16;
    const GAP = 4;

    const menuStyle: React.CSSProperties =
        level === 0 && triggerRect
            ? (() => {
                  // Computing the height to make sure the menu is fully visible
                  const estimatedHeight = items.length * ESTIMATED_ITEM_HEIGHT + MENU_PADDING;
                  const spaceBelow = window.innerHeight - triggerRect.bottom;
                  const openUpward = spaceBelow < estimatedHeight + GAP;
                  return {
                      position: "fixed",
                      left: triggerRect.left,
                      zIndex: 999999,
                      ...(openUpward
                          ? { bottom: window.innerHeight - triggerRect.top + GAP }
                          : { top: triggerRect.bottom + GAP }),
                  };
              })()
            : {};

    return (
        <ul
            className={level === 0 ? styles.portalMenu : styles.menu}
            ref={menuRef}
            style={menuStyle}
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMenuMouseLeave}
            onWheel={(e) => e.stopPropagation()}
        >
            {items.map((item) => (
                <li
                    key={item.id}
                    className={`${styles.menuItem} ${item.children ? styles.hasChildren : ""} `}
                    onMouseEnter={(e) => {
                        if (item.children) {
                            handleItemMouseEnter(item.id, e.currentTarget);
                        }
                    }}
                    onMouseLeave={() => {
                        if (item.children) {
                            handleItemMouseLeave();
                        }
                    }}
                >
                    <button type="button" onClick={() => !item.disabled && onSelect(item.id)}>
                        {item.displayValue}
                        {item.children && <RightChevronIcon />}
                    </button>
                    {item.children && hoveredItem === item.id && (
                        <div
                            className={`${styles.submenuContainer} ${
                                submenuPositions[item.id] === "left" ? styles.submenuLeft : styles.submenuRight
                            }`}
                            onMouseEnter={handleMenuMouseEnter}
                            onMouseLeave={handleMenuMouseLeave}
                        >
                            <MenuItems items={item.children} onSelect={onSelect} level={level + 1} />
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}

export default NestedDropdown;
